import React from "react";
import { motion } from "framer-motion";
import { Shield, Users } from "lucide-react";

function AboutCompany() {
  const features = [
    {
      icon: Users,
      title: "Pelatihan & Pemberdayaan OAP",
      description:
        "Program pengembangan staf lokal untuk meningkatkan kemampuan keamanan dan profesionalisme.",
    },
  ];

  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: (i = 1) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.15, duration: 0.7, ease: "easeOut" },
    }),
  };

  return (
    <section className="relative bg-white text-slate-900 py-32 px-6 md:px-16 overflow-hidden">
      {/* 🌿 Background Animasi */}
      <motion.div
        className="absolute inset-0 overflow-hidden pointer-events-none"
        animate={{
          backgroundPosition: ["0% 0%", "100% 100%", "0% 0%"],
        }}
        transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
      >
        <motion.div
          animate={{ scale: [1, 1.05, 1], opacity: [0.8, 1, 0.8] }}
          transition={{ duration: 6, repeat: Infinity }}
          className="absolute top-1/4 left-1/4 w-96 h-96 bg-green-300/10 rounded-full filter blur-[120px]"
        ></motion.div>
        <motion.div
          animate={{ scale: [1, 1.07, 1], opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 7, repeat: Infinity, delay: 1 }}
          className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal-300/10 rounded-full filter blur-[120px]"
        ></motion.div>
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,128,0,0.08)_2px,transparent_2px),linear-gradient(90deg,rgba(0,128,0,0.08)_2px,transparent_2px)] bg-[size:100px_100px]" />
      </motion.div>

      {/* ✴️ Header */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="text-center mb-20 relative z-10 max-w-4xl mx-auto"
      >
        <motion.div
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 border border-green-200 rounded-full backdrop-blur-sm mb-6"
        >
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span className="text-green-600 text-sm font-mono uppercase tracking-wider">
            Keamanan
          </span>
        </motion.div>

        <motion.h2
          whileHover={{ scale: 1.02 }}
          className="text-5xl md:text-7xl font-black mb-6 tracking-tight"
        >
          <span className="text-slate-900">Keamanan & </span>
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 via-teal-500 to-green-700">
            Profesional
          </span>
          <span className="text-green-600">Profesional</span>
        </motion.h2>

        <div className="h-1 w-32 bg-gradient-to-r from-green-500 to-teal-500 rounded-full mx-auto mb-8"></div>

        <p className="text-xl md:text-2xl text-slate-700 max-w-3xl mx-auto leading-relaxed font-light">
          Penyedia jasa keamanan profesional yang berfokus pada proteksi aset
          dan keselamatan lingkungan. Dibangun atas{" "}
          <span className="text-slate-900 font-semibold">integritas</span> dan
          didukung oleh{" "}
          <span className="text-slate-900 font-semibold">
            tim satpam terlatih
          </span>{" "}
          yang siap siaga.
        </p>
      </motion.div>

      {/* ⚙️ Grid 2 Kolom (atas) */}
      <div className="relative z-10 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-start mb-20">
        {/* Visual */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.div
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.4 }}
            className="relative group"
          >
            <div className="absolute -inset-1 bg-gradient-to-r from-green-500 to-teal-500 rounded-3xl blur-xl opacity-20 transition duration-500"></div>

            <div className="relative h-96 rounded-3xl overflow-hidden border border-gray-200 bg-white/90 backdrop-blur-xl flex items-center justify-center">
              <motion.div
                animate={{ y: [0, -6, 0] }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <Shield className="w-24 h-24 text-green-500" />
              </motion.div>
            </div>

            <div className="absolute bottom-6 left-6 right-6 p-4 bg-white/70 backdrop-blur-md rounded-xl border border-gray-200">
              <p className="text-sm text-slate-700 font-medium">
                Tim Satpam Profesional & Siap Siaga
              </p>
              <p className="text-xs text-slate-500 mt-1 font-mono">
                Melindungi aset dan lingkungan 24/7
              </p>
            </div>
          </motion.div>
        </motion.div>

        {/* Text Content */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="space-y-10"
        >
          <div>
            <h3 className="text-4xl font-bold mb-6 flex items-center gap-3 text-slate-900">
              <motion.span
                animate={{ height: ["2rem", "2.4rem", "2rem"] }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="w-2 bg-gradient-to-b from-green-500 to-teal-500 rounded-full"
              ></motion.span>
              Perjalanan Menuju Keunggulan
            </h3>
            <p className="text-lg text-slate-700 leading-relaxed mb-8">
              <span className="text-slate-900 font-semibold">Soney Raya</span>{" "}
              berdiri untuk menyediakan perlindungan nyata bagi aset dan
              properti. Kami mengintegrasikan{" "}
              <span className="text-green-500">tenaga satpam profesional</span>{" "}
              dengan protokol keamanan modern, menciptakan lapisan perlindungan
              yang andal dan selalu siap siaga.
            </p>
          </div>

          <motion.div variants={fadeUp} custom={3} className="space-y-6">
            <h4 className="text-2xl font-bold mb-4 text-slate-900">
              Fokus Inti 2025
            </h4>
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  whileHover={{ scale: 1.02, y: -2 }}
                  transition={{ duration: 0.3 }}
                  className="group relative"
                >
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-green-500 to-teal-500 rounded-xl blur opacity-0 group-hover:opacity-30 transition duration-500"></div>
                  <div className="relative p-5 bg-white border border-gray-200 rounded-xl shadow-sm">
                    <div className="flex items-start gap-4">
                      <motion.div
                        animate={{ y: [0, -5, 0] }}
                        transition={{
                          duration: 3,
                          repeat: Infinity,
                          ease: "easeInOut",
                        }}
                        className="w-12 h-12 rounded-lg bg-gradient-to-br from-green-500 to-teal-500 flex items-center justify-center flex-shrink-0"
                      >
                        <Icon className="w-6 h-6 text-white" />
                      </motion.div>
                      <div className="flex-1">
                        <h5 className="text-lg font-semibold text-slate-900 mb-1">
                          {feature.title}
                        </h5>
                        <p className="text-sm text-slate-700 leading-relaxed">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </motion.div>
      </div>

      {/* 💬 Quote Full Width */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        whileHover={{ scale: 1.02 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 max-w-5xl mx-auto"
      >
        {/* Glow animasi lembut */}
        <motion.div
          animate={{ opacity: [0.1, 0.25, 0.1], scale: [1, 1.03, 1] }}
          transition={{ duration: 4, repeat: Infinity }}
          className="absolute -inset-0.5 bg-gradient-to-r from-green-500 to-teal-500 rounded-2xl blur-lg"
        ></motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="relative p-10 bg-white border border-gray-200 rounded-2xl shadow-xl text-center overflow-hidden"
        >
          <motion.div
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            className="text-green-500 text-6xl mb-4 opacity-70"
          >
            "
          </motion.div>
          <motion.p
            animate={{ opacity: [0.9, 1, 0.9] }}
            transition={{ duration: 6, repeat: Infinity }}
            className="text-xl text-slate-700 font-light leading-relaxed mb-4 max-w-3xl mx-auto"
          >
            Kami mengubah pengamanan dari tugas reaktif menjadi perlindungan
            proaktif, didukung oleh{" "}
            <span className="text-slate-900 font-semibold">
              tenaga profesional lokal
            </span>{" "}
            yang selalu siap siaga.
          </motion.p>
          <motion.div
            animate={{ opacity: [0.6, 1, 0.6] }}
            transition={{ duration: 4, repeat: Infinity }}
            className="flex items-center justify-center gap-3"
          >
            <div className="h-px w-24 bg-gradient-to-r from-green-500/50 to-transparent"></div>
            <p className="text-sm text-slate-500 font-mono">Soney Raya</p>
            <div className="h-px w-24 bg-gradient-to-l from-green-500/50 to-transparent"></div>
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
}

export default AboutCompany;
